import { Component, OnInit, Input, OnChanges, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, ModalDismissReasons } from '@ng-bootstrap/ng-bootstrap';

import { AutocompleteResult } from '../../../models/autocomplete-result';
import { PhoneNumber } from '../../../models/phone-number';

import { ContactStoreService } from '../../../services/contacts/contact-store.service';
import { ModalService } from '../../../services/common/modal.service';
import { NotyService } from '../../../services/common/noty.service';
import { PhoneNumberService } from '../../../services/contacts/phone-number.service';

@Component({
  selector: 'app-phone-number-index',
  templateUrl: './phone-number-index.component.html',
  styleUrls: ['./phone-number-index.component.scss'],
})
export class PhoneNumberIndexComponent implements OnInit, OnChanges {
  /**
   * Contact id from parent component
   */
  @Input() contactId: number;

  /**
   * Alternate phone numbers for phone number delete confirmation, this is filled when a phone number delete needs additional confirmation
   * @type {Array<AutocompleteResult>}
   */
  alternatePhoneNumbers: Array<AutocompleteResult> = [];

  /**
   * Warning message if a phone number delete needs additional confirmation
   * @type {string}
   */
  deleteWarningMessage: string;

  /**
   * Phone Number list
   * @type {Array<PhoneNumber>}
   */
  phoneNumbers: Array<PhoneNumber> = [];

  /**
   * Indicates if phone numbers are being loaded
   * @type {boolean}
   */
  loadingPhoneNumbers: boolean = false;

  /**
   * Indicates if new phone number form is to be showed
   * @type {boolean}
   */
  showNewPhoneNumberForm: boolean = false;

  /**
   * Sortable options of Phone Numbers sorting using Angular-sortablejs
   * @type {any}
   */
  sortableOptions: any = {
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    ghostClass: 'sortable-ghost',
    handle: '.alias-sort-handle',
    onUpdate: (event: any) => {
      this.updatePhoneNumbersPosition();
    },
  };

  /**
   * Phone number delete second confirm modal template ref
   * @param {[type]} 'confirmDeleteModal' Template name in HTML
   */
  @ViewChild('confirmDeleteModal', { static: true }) confirmDeleteModalTemplateRef: TemplateRef<any>;

  constructor(private contactStoreService: ContactStoreService, private modalService: ModalService, private ngbModalService: NgbModal, private notyService: NotyService, private phoneNumberService: PhoneNumberService) {}

  // Component lifecycle methods

  /**
   * Called on Component initialization
   */
  ngOnInit() {}

  /**
   * Called when component variables change
   * @param {SimpleChanges} changes changes containing all variables changed
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.contactId && changes.contactId.currentValue) this.loadPhoneNumbers();
  }

  /**
   * Delete Phone Number
   * @param {PhoneNumber} phoneNumber Phone number to delete
   * @param {number} index Position of phone number in array
   */
  deletePhoneNumber(phoneNumber: PhoneNumber, index: number) {
    console.log('Delete phone number called');
    console.log('deletePhoneNumber phoneNumber', phoneNumber);
    console.log('deletePhoneNumber index', index);
    this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-danger';
    this.modalService.confirmDialogComponent.open(`<p>Delete Phone Number - <strong class='text-danger'>${phoneNumber.number} ${phoneNumber.extension || ''}</strong></p>`).result.then(
      (result) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
        if (result == 'confirmed') {
          phoneNumber.deleting = true;
          console.log('phoneNumber.id', phoneNumber.id);
          this.phoneNumberService
            .delete(phoneNumber.id)
            .then((response_json) => {
              console.log('phoneNumber Service delete success', response_json);
              if (response_json.success) {
                phoneNumber.deleting = false;
                this.notyService.notify(response_json.text || `Phone number deleted ${phoneNumber.number} ${phoneNumber.extension || ''}`, 'warning');
                this.phoneNumbers.splice(index, 1);
              } else if (response_json.warning) this.deletePhoneNumberConfirm(phoneNumber, index, response_json.warning, response_json.alternate_phone_numbers);
              else this.notyService.notify(response_json.text || `Error deleting phone number - ${phoneNumber.number} ${phoneNumber.extension || ''}`, 'error');
            })
            .catch((deleteErr) => {
              console.log('phoneNumber Service delete error', deleteErr);
              phoneNumber.deleting = false;
              this.notyService.notify('Error deleting phone number', 'error');
            });
        }
      },
      (reason) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
      }
    );
  }

  /**
   * Delete Phone Number confirmed when phone number is used in involvements
   * @param {PhoneNumber} phoneNumber Phone number to delete
   * @param {number} index Position of phone number in array
   * @param {string} warningMessage Message from server on phone number deletion before confirmation
   */
  deletePhoneNumberConfirm(phoneNumber: PhoneNumber, index: number, warningMessage: string, alternatePhoneNumbers: Array<AutocompleteResult>) {
    console.log('Delete phone number confirm called');
    this.alternatePhoneNumbers = alternatePhoneNumbers || [];
    this.alternatePhoneNumbers.push({ label: 'None' } as AutocompleteResult);
    this.deleteWarningMessage = warningMessage || '';
    this.ngbModalService.open(this.confirmDeleteModalTemplateRef).result.then(
      (result) => {
        let alternatePhoneNumberId: number | undefined = undefined;
        for (let alternatePhoneNumber of this.alternatePhoneNumbers)
          if (alternatePhoneNumber.checked) {
            alternatePhoneNumberId = Number(alternatePhoneNumber.value);
            break;
          }
        phoneNumber.deleting = true;
        this.phoneNumberService
          .deleteConfirm(phoneNumber.id, alternatePhoneNumberId)
          .then((response_json) => {
            console.log('phoneNumberService deleteConfirm', response_json);
            phoneNumber.deleting = false;
            this.notyService.notify(response_json.text || `Phone number deleted ${phoneNumber.number} ${phoneNumber.extension || ''}`, 'warning');
            this.phoneNumbers.splice(index, 1);
          })
          .catch(() => {
            phoneNumber.deleting = false;
            this.notyService.notify('Error deleting phone number', 'error');
          });
        this.alternatePhoneNumbers = [];
        this.deleteWarningMessage = '';
      },
      (reason) => {
        // Cancelled
        this.alternatePhoneNumbers = [];
        this.deleteWarningMessage = '';
      }
    );
  }

  /**
   * Form saved
   * @param {boolean} event Indicates if formed saved successfully
   */
  formSaved(event: boolean, phoneNumber: PhoneNumber | null = null) {
    if (event && !phoneNumber) {
      if (!phoneNumber) this.loadPhoneNumbers();
    } else if (!!event && phoneNumber) this.loadPhoneNumbers();
    if (!phoneNumber) this.showNewPhoneNumberForm = false;
    else phoneNumber.editing = false;
  }

  /**
   * Load Phone Numbers
   */
  loadPhoneNumbers() {
    this.loadingPhoneNumbers = true;
    this.phoneNumberService
      .index({ contact_id: this.contactId, angular: true })
      .then((response_json) => {
        console.log('loadPhoneNumbers success', response_json);
        this.loadingPhoneNumbers = false;
        this.phoneNumbers = response_json;
      })
      .catch((loadPhoneNumbersEr) => {
        console.log('loadPhoneNumbers error', loadPhoneNumbersEr);
        this.loadingPhoneNumbers = false;
      });
  }

  /**
   * Select alternate phone number for replacement on delete
   * @param {AutocompleteResult} alternatePhoneNumber Alternate phone number to delete
   */
  selectAlternatePhoneNumber(alternatePhoneNumber: AutocompleteResult) {
    for (let alternatePhoneNumber of this.alternatePhoneNumbers) alternatePhoneNumber.checked = false;
    alternatePhoneNumber.checked = true;
  }

  /**
   * Update positions of phone numbers
   */
  updatePhoneNumbersPosition() {
    let phoneNumberIds: Array<number> = [];
    for (let phoneNumber of this.phoneNumbers) phoneNumberIds.push(phoneNumber.id);
    this.phoneNumberService
      .updatePositions(this.contactId, phoneNumberIds)
      .then((response_json) => {
        if (response_json.success && this.contactStoreService && this.contactStoreService.contactShowComponent) this.contactStoreService.contactShowComponent.loadContact();
        else this.notyService.notify('Error in sorting phone numbers', 'error');
      })
      .catch(() => {
        this.notyService.notify('Error in sorting phone numbers', 'error');
      });
  }
}
